import React, { useRef, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useGrnDetailContext } from 'providers/GrnDetailProvider';

import Table from 'components/common/Table';
import IconButton from 'components/common/IconButton';
import GrnLineAddModal from 'components/purchase-order/grn/GrnDetails/GrnLineAddModal';

import { grnApi } from 'api';

import { GRN_LINE_LIST_COLUMNS } from 'constants/grn';

const GrnLines = ({ onGRNLineUpdate }) => {
  const { grnId } = useParams();
  const { grnData } = useGrnDetailContext();

  const tableRef = useRef();
  const [showAddModal, setShowAddModal] = useState(false);

  const grnType = useMemo(() => {
    if (grnData?.gate_pass) {
      return 'gatepass';
    }
    return 'po'; // Default to 'po'
  }, [grnData]);

  const columns = useMemo(() => {
    const skuColumn = {
      accessorKey: 'sku',
      header: grnType === 'po' ? 'Vendor SKU' : 'SKU',
      enableSorting: false,
      meta: {
        valueType: 'string',
      },
      cell: ({ row: { original } }) => {
        if (grnType === 'po') {
          return (
            <span>{original.po_line?.vendor_product?.vendor_sku || '-'}</span>
          );
        }
        return <span>{original.product?.sku || '-'}</span>;
      },
    };
    // The rest of the columns from GRN_LINE_LIST_COLUMNS, excluding the first one (id)
    const otherColumns = GRN_LINE_LIST_COLUMNS.slice(1);

    return [skuColumn, ...otherColumns];
  }, [grnType]);

  const handleAddModalOpen = useCallback(() => {
    setShowAddModal(true);
  }, []);

  const handleAddModalClose = useCallback(() => {
    setShowAddModal(false);
  }, []);

  const handleRemoveGRNLines = useCallback(
    async selectedRows => {
      const grnLineIds = selectedRows.map(row => row.id);

      const response = await grnApi.deleteGRNLines(grnId, {
        ids: grnLineIds,
      });

      onGRNLineUpdate?.();

      return {
        result: response,
        shouldRefetch: true,
      };
    },
    [onGRNLineUpdate, grnId],
  );

  const handleAddGRNLines = useCallback(
    async addedLines => {
      try {
        await grnApi.addGRNLines(grnId, addedLines, grnType);

        tableRef.current?.refetch();

        toast.success('Successfully add GRN Lines');

        onGRNLineUpdate?.();
      } catch (error) {
        toast.error('Fail to add GRN Lines');
      }
    },
    [onGRNLineUpdate, grnId, grnType],
  );

  const headerActions = (
    <>
      <IconButton
        variant="falcon-default"
        size="sm"
        icon="plus"
        transform="shrink-3"
        onClick={handleAddModalOpen}
      >
        <span className="d-none d-sm-inline-block">Add Line</span>
      </IconButton>
    </>
  );

  const bulkActions = [
    {
      value: 'remove_grn_lines',
      label: 'Remove',
      variant: 'falcon-default',
      className: 'text-danger',
      successMessage: 'Successfully remove GRN Lines',
      errorMessage: 'Fail to remove GRN Lines',
      handler: handleRemoveGRNLines,
    },
  ];

  const isEditableStatus = status => {
    return status === 'draft' || status === 'pending';
  };

  const exportApi = async () => {
    return grnApi.getGrnLineExport(grnId);
  };

  return (
    <>
      <Table
        ref={tableRef}
        title="GRN Lines"
        apiService={grnApi}
        exportApi={exportApi}
        sortable={false}
        columns={columns} // Use dynamically generated columns
        fetchConfig={{
          method: 'getGRNLines',
          transformResponse: response => ({
            data: response || [],
            totalItems: response?.length || 0,
          }),
        }}
        resourceId={grnId}
        enablePagination={false}
        bulkActions={
          isEditableStatus(grnData?.grn_status) ? bulkActions : undefined
        }
        headerActions={
          isEditableStatus(grnData?.grn_status) ? headerActions : undefined
        }
      />

      <GrnLineAddModal
        show={showAddModal}
        onHide={handleAddModalClose}
        onSave={handleAddGRNLines}
        grnId={grnId}
        grnType={grnType} // Pass grnType to the modal
      />
    </>
  );
};

export default GrnLines;
